import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-row justify-between items-center" }
const _hoisted_2 = {
  "data-dusk": "patient-form-title",
  class: "text-lg font-semibold text-gray-900"
}
const _hoisted_3 = { class: "grid mt-10 grid-cols-1 gap-y-8 gap-x-6 md:grid-cols-2" }
const _hoisted_4 = { class: "grid grid-cols-1 gap-y-8 gap-x-6 mt-8 md:grid-cols-3" }

import BaseTextInput from '@/lib/components/Input/BaseTextInput.vue';
import PhoneNumberInput from '@/lib/components/Input/PhoneNumberInput.vue';
import BaseDatePicker from '@/lib/components/Date/BaseDatePicker.vue';
import BaseSelect from '@/lib/components/Select/BaseSelect.vue';
import SelectInput from '@/lib/components/Select/BaseSelectInput.vue';
import { computed, onBeforeMount, onBeforeUnmount, PropType, ref, watch } from 'vue';
import { Address, Ethnicity, Patient } from '@/models';
import AddressAutocomplete from '@/lib/components/Addresses/AddressAutocomplete.vue';
import { removeNonPrintableCharactersFromString } from '@/helpers/strings.helper';
import dayjs from 'dayjs';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES, sexOptions } from '@/constants';
import { useI18n } from 'vue-i18n';
import { IOption } from '@/lib';
import axios from 'axios';
import { EthnicityService } from '@/services/api';
import { useNotificationStore } from '@/stores/notification.store';
import {
  countErrors,
  validName,
  required,
  validate,
  validDateOfBirth,
  validEmail
} from '@/custom/menicon/helpers/validators.helper';
import { i18n } from '@/i18n/i18n';

type FormError = Record<string, string[]> | string[];

export default /*@__PURE__*/_defineComponent({
  __name: 'PatientDetailsForm',
  props: {
  title: {
    type: String,
    default() {
      return i18n.global.t('platform.patient.details');
    }
  },
  patient: {
    type: Object as PropType<Patient>,
    default: null
  },
  errors: {
    type: Object as PropType<Record<string, FormError>>,
    default: () => ({})
  }
},
  emits: ["update:errors"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();
const isPatientEmailOptional = !isFeatureFlagEnabled(FEATURES.VIRTUAL_DOCTOR);
const today = dayjs().format('YYYY-MM-DD');
const minDateOfBirth = dayjs().subtract(150, 'year').format('YYYY-MM-DD');
const controller = new AbortController();
const ethnicityService = new EthnicityService();
const notificationStore = useNotificationStore();

const ethnicities = ref<Ethnicity[]>([]);
const phoneNumberInput = ref<InstanceType<typeof PhoneNumberInput> | null>(null);
const firstName = ref<string>(props.patient?.first_name ?? '');
const lastName = ref<string>(props.patient?.last_name ?? '');
const contactNumber = ref<string>(props.patient?.contact_number ?? '');
const emailAddress = ref<string>(props.patient?.email ?? '');
const dateOfBirth = ref<string>(props.patient?.date_of_birth ?
  dayjs.utc(removeNonPrintableCharactersFromString(props.patient.date_of_birth)
    .substring(0, 23))
    .format('YYYY-MM-DD') : '');
const sex = ref<string>(props.patient?.sex ?? '');
const ethnicityId = ref<string>(props.patient?.ethnicity_id ?? props.patient?.ethnicity?.id ?? '');
const address = ref<Address>(props.patient?.address ?? {
  formatted_address: ''
});

const rules = computed(() => ({
  first_name: {
    required,
    validName: () => validName(patientData.value.first_name, t('platform.error.first-name-error'))
  },
  last_name: {
    required,
    validName: () => validName(patientData.value.last_name, t('platform.error.last-name-error'))
  },
  date_of_birth: {
    required,
    validDateOfBirth
  },
  sex: { required },
  ...isPatientEmailOptional ? { email: { validEmail } } : { email: { required, validEmail } }
}));
const errorsCount = computed<number>(() => countErrors(props.errors));
const ethnicityOptions = computed<IOption[]>(() => ethnicities.value.map((ethnicity) => ({
  value: ethnicity.id,
  label: ethnicity.title
})));
const isFormValid = computed<boolean>(() => !!(
  firstName.value &&
    lastName.value &&
    dateOfBirth.value &&
  (isPatientEmailOptional || emailAddress.value) && sex.value.length &&
    errorsCount.value === 0 &&
    (!contactNumber.value || phoneNumberInput.value?.isValid)
));
const patientData = computed<Partial<Patient>>(() => ({
  first_name: removeNonPrintableCharactersFromString(firstName.value),
  last_name: removeNonPrintableCharactersFromString(lastName.value),
  date_of_birth: removeNonPrintableCharactersFromString(dateOfBirth.value),
  sex: sex.value,
  contact_number: contactNumber.value,
  email: emailAddress.value,
  address: address.value,
  ethnicity_id: ethnicityId.value ?? null
}));

watch(() => props.patient, () => {
  firstName.value = removeNonPrintableCharactersFromString(props.patient.first_name);
  lastName.value = removeNonPrintableCharactersFromString(props.patient.last_name);
  sex.value = props.patient.sex;
  contactNumber.value = props.patient.contact_number;
  emailAddress.value = props.patient.email;
  address.value = props.patient.address;
  ethnicityId.value = props.patient.ethnicity_id ?? props.patient.ethnicity?.id ?? null;
  dateOfBirth.value = dayjs.utc(removeNonPrintableCharactersFromString(props.patient.date_of_birth).substring(0, 23)).format('YYYY-MM-DD');
});

onBeforeMount(async () => {
  await fetchEthnicities();
});
onBeforeUnmount(() => {
  controller.abort();
});

const fetchEthnicities = async () => {
  try {
    ethnicities.value = (
      await ethnicityService.index({
        signal: controller.signal
      })
    ).data;
  } catch (e) {
    if (!axios.isCancel(e)) {
      await notificationStore.addErrorNotification({
        title: t('platform.error.fetch-data') as string
      });
    }
  }
};
const onEmailChange = (value: string) => {
  if (props.errors?.email?.length) {
    validateEmail(value);
  }
};
const onNameChange = (name: string, isLastName = false) => {
  if (isLastName ? props.errors.last_name?.length : props.errors.first_name?.length) {
    validatePatientName(name, isLastName);
  }
};
const validateDate = (date: string) => {
  emit('update:errors', {
    ...props.errors,
    ...validate({
      date_of_birth: rules.value.date_of_birth
    }, { date_of_birth: date })
  });
};
const validatePatientName = (name: string, isLastName = false) => {
  const key = isLastName ? 'last_name' : 'first_name';
  emit('update:errors', {
    ...props.errors,
    ...validate({
      [key]: {
        required,
        validName: () => validName(name, t(`platform.error.${isLastName ? 'last' : 'first'}-name-error`))
      }
    }, { [key]: name })
  });
};
const validateEmail = (email: string) => {
  emit('update:errors', {
    ...props.errors,
    ...validate({
      email: rules.value.email
    }, { email })
  });
};
const deleteError = (key: string) => {
  emit('update:errors', {
    ...props.errors,
    [key]: []
  });
};
const deleteAddressError = () => {
  emit('update:errors', {
    ...props.errors,
    address: {
      formatted_address: []
    },
    'address.formatted_address': []
  });
};
const validateForm = () => {
  emit('update:errors', {
    ...props.errors,
    ...validate(rules.value, patientData.value)
  });
};
__expose({
  isFormValid,
  patientData,
  validateForm
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h1", _hoisted_2, _toDisplayString(__props.title), 1),
      _renderSlot(_ctx.$slots, "default")
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(BaseTextInput, {
        modelValue: firstName.value,
        "onUpdate:modelValue": [
          _cache[0] || (_cache[0] = ($event: any) => ((firstName).value = $event)),
          _cache[1] || (_cache[1] = ($event: any) => (onNameChange($event as string)))
        ],
        "data-dusk": "first-name-input",
        label: _ctx.$t('platform.patient.given-names'),
        placeholder: _ctx.$t('platform.patient.given-names'),
        error: __props.errors.first_name?.[0],
        onBlur: _cache[2] || (_cache[2] = ($event: any) => (validatePatientName(firstName.value)))
      }, null, 8, ["modelValue", "label", "placeholder", "error"]),
      _createVNode(BaseTextInput, {
        modelValue: lastName.value,
        "onUpdate:modelValue": [
          _cache[3] || (_cache[3] = ($event: any) => ((lastName).value = $event)),
          _cache[4] || (_cache[4] = ($event: any) => (onNameChange($event as string, true)))
        ],
        "data-dusk": "last-name-input",
        label: _ctx.$t('platform.patient.family-name'),
        placeholder: _ctx.$t('platform.patient.family-name'),
        error: __props.errors.last_name?.[0],
        onBlur: _cache[5] || (_cache[5] = ($event: any) => (validatePatientName(lastName.value, true)))
      }, null, 8, ["modelValue", "label", "placeholder", "error"]),
      _createVNode(PhoneNumberInput, {
        id: "phone",
        ref_key: "phoneNumberInput",
        ref: phoneNumberInput,
        modelValue: contactNumber.value,
        "onUpdate:modelValue": [
          _cache[6] || (_cache[6] = ($event: any) => ((contactNumber).value = $event)),
          _cache[7] || (_cache[7] = ($event: any) => (deleteError('contact_number')))
        ],
        "is-optional": "",
        "data-dusk": "phone-number",
        label: _ctx.$t('platform.patient.contact-number'),
        error: __props.errors.contact_number?.[0]
      }, null, 8, ["modelValue", "label", "error"]),
      _createVNode(BaseTextInput, {
        modelValue: emailAddress.value,
        "onUpdate:modelValue": [
          _cache[8] || (_cache[8] = ($event: any) => ((emailAddress).value = $event)),
          _cache[9] || (_cache[9] = ($event: any) => (onEmailChange($event as string)))
        ],
        "is-optional": isPatientEmailOptional,
        label: _ctx.$t('platform.patient.email-address'),
        "data-dusk": "email-input",
        type: "email",
        placeholder: _ctx.$t('platform.patient.email-address'),
        error: __props.errors.email?.[0],
        onBlur: _cache[10] || (_cache[10] = ($event: any) => (validateEmail(emailAddress.value)))
      }, null, 8, ["modelValue", "label", "placeholder", "error"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(BaseDatePicker, {
        modelValue: dateOfBirth.value,
        "onUpdate:modelValue": [
          _cache[11] || (_cache[11] = ($event: any) => ((dateOfBirth).value = $event)),
          _cache[12] || (_cache[12] = ($event: any) => (validateDate($event)))
        ],
        label: _ctx.$t('platform.patient.date-of-birth'),
        "min-date": _unref(minDateOfBirth),
        "max-date": _unref(today),
        error: __props.errors.date_of_birth?.[0]
      }, null, 8, ["modelValue", "label", "min-date", "max-date", "error"]),
      _createVNode(BaseSelect, {
        modelValue: sex.value,
        "onUpdate:modelValue": [
          _cache[13] || (_cache[13] = ($event: any) => ((sex).value = $event)),
          _cache[14] || (_cache[14] = ($event: any) => (deleteError('sex')))
        ],
        label: _ctx.$t('platform.patient.sex'),
        "data-dusk": "sex-select",
        placeholder: _ctx.$t('platform.common.select-option'),
        options: _unref(sexOptions)(),
        error: __props.errors.sex?.[0]
      }, null, 8, ["modelValue", "label", "placeholder", "options", "error"]),
      _createVNode(SelectInput, {
        modelValue: ethnicityId.value,
        "onUpdate:modelValue": [
          _cache[15] || (_cache[15] = ($event: any) => ((ethnicityId).value = $event)),
          _cache[16] || (_cache[16] = ($event: any) => (deleteError('ethnicity_id')))
        ],
        label: _ctx.$t('platform.patient.ethnicity'),
        placeholder: _ctx.$t('platform.common.select-option'),
        "data-dusk": "ethnicity-select",
        options: ethnicityOptions.value,
        "is-optional": "",
        error: __props.errors.ethnicity_id?.[0],
        clear: ""
      }, null, 8, ["modelValue", "label", "placeholder", "options", "error"])
    ]),
    _createVNode(AddressAutocomplete, {
      modelValue: address.value,
      "onUpdate:modelValue": [
        _cache[17] || (_cache[17] = ($event: any) => ((address).value = $event)),
        _cache[18] || (_cache[18] = ($event: any) => (deleteAddressError()))
      ],
      label: _ctx.$t('platform.common.address'),
      class: "mt-4",
      placeholder: _ctx.$t('platform.patient.address'),
      "is-optional": "",
      error: __props.errors['address.formatted_address']?.[0] ?? (__props.errors.address as Record<string, string[]>)?.formatted_address?.[0] 
    }, null, 8, ["modelValue", "label", "placeholder", "error"])
  ]))
}
}

})